import React, { useEffect, useState, createContext, useContext } from 'react';
type Language = 'en' | 'ru' | 'ua' | 'pl';
type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  getPath: (path: string) => string;
};
const translations = {
  en: {
    // Navigation
    'nav.getStarted': 'Get a free preview',
    'nav.home': 'Home',
    'nav.services': 'Pricing',
    'nav.referral': 'Referral Program',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',
    // Cookie Banner
    'cookie.title': 'We use cookies',
    'cookie.description': 'We use cookies to improve your experience on our site, analyze traffic, and personalize content.',
    'cookie.accept': 'Accept All',
    'cookie.decline': 'Decline',
    'cookie.learnMore': 'Learn More',
    // Hero
    'hero.badge': 'Professional web agency',
    'hero.title': 'Modern website',
    'hero.titleHighlight': 'for €299',
    'hero.subtitle': 'First, we create a free website preview so you can see how your future website will look. Then you tell us what to change — we apply the updates. If you want to use the website, you pay €299.',
    'hero.bullet1': 'Free website preview before payment',
    'hero.bullet2': '€299 — website development fee if you like the result',
    'hero.bullet3': 'We work worldwide (fully remote)',
    'hero.cta': 'Get a free website preview',
    'hero.referral': 'Referral Program',
    // Pricing
    'pricing.title': 'Pricing',
    'pricing.titleHighlight': '& terms',
    'pricing.subtitle': 'Start with a free preview. Pay only if you want to use the website.',
    'pricing.demo.title': 'Free website preview',
    'pricing.demo.label': 'FREE',
    'pricing.demo.desc': 'See your future website before you pay.',
    'pricing.demo.b1': 'Preview tailored to your business',
    'pricing.demo.b2': 'You can request changes',
    'pricing.demo.b3': "Don't like it — don't pay",
    'pricing.demo.cta': 'Get a free preview',
    'pricing.launch.title': 'Website development',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(one-time)',
    'pricing.launch.desc': 'If you like the result, we finalize the website and you get the ready-to-use version.',
    'pricing.launch.b1': 'Desktop + mobile design',
    'pricing.launch.b2': 'Updates based on your feedback',
    'pricing.launch.b3': 'Lead form + notifications',
    'pricing.launch.cta': 'Get the website for €299',
    // How It Works
    'how.title': 'How it',
    'how.titleHighlight': 'works',
    'how.step1.title': 'Tell us about your business',
    'how.step1.note': 'Takes 2 minutes — what you do and what website you need',
    'how.step2.title': 'We create a free website preview',
    'how.step2.note': 'We show the design and structure. You get a link in 1–3 days',
    'how.step3.title': 'You tell us what to change',
    'how.step3.note': 'Text, sections, style — we update everything',
    'how.step4.title': "When you're happy with it, you pay €299",
    'how.step4.note': 'Payment only after you say: "Yes, this works for me"',
    'how.step5.title': 'We connect your domain and publish the website',
    'how.step5.note': 'Your website goes live and starts collecting inquiries',
    'how.note': "If you don't like the website — you don't pay.",
    // FAQ
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    'faq.subtitle': 'Everything you need to know about working with our team',
    'faq.q1': 'What is a free preview?',
    'faq.a1': 'A preliminary version of your website so you can review the design and structure before paying.',
    'faq.q2': 'When do I pay €299?',
    'faq.a2': 'Only if you like the website and want to use it.',
    'faq.q3': 'Can I request changes?',
    'faq.a3': 'Yes. You send feedback — we update the website.',
    'faq.q4': 'What do I get after payment?',
    'faq.a4': 'A ready-to-use website connected to your domain, with a lead form.',
    'faq.q5': 'Do you work worldwide?',
    'faq.a5': 'Yes, fully remote.',
    // Testimonials
    'testimonials.title': 'What clients',
    'testimonials.titleHighlight': 'say',
    'testimonials.subtitle': 'Real feedback from businesses we helped launch online',
    'testimonials.r1.name': 'Sarah',
    'testimonials.r1.text': 'I was honestly surprised by the quality for €299. They built the website first and sent me a link to check it. I asked for a few text changes — done. I paid only when I decided to actually use it and connect my domain.',
    'testimonials.r1.business': 'Beauty specialist',
    'testimonials.r2.name': 'Alex',
    'testimonials.r2.text': 'No upfront payment. They showed me the website first, I asked to adjust a couple of sections, and I paid only after I confirmed I wanted to use it for my business.',
    'testimonials.r2.business': 'Local handyman',
    'testimonials.r3.name': 'James',
    'testimonials.r3.text': "It's simple: you see the website first, then decide. I only paid when I was ready to use it for my consulting practice.",
    'testimonials.r3.business': 'Business consultant',
    // Consultation
    'consultation.title': 'Get your',
    'consultation.titleHighlight': 'free preview',
    'consultation.subtitle': "Tell us what you need — we'll send a preview link.",
    'consultation.benefit1': 'Free preview',
    'consultation.benefit2': 'Fast delivery',
    'consultation.benefit3': 'No commitment',
    'consultation.name': 'Name',
    'consultation.namePlaceholder': 'Your name',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'your@email.com',
    'consultation.desc': 'Project description',
    'consultation.descPlaceholder': 'Tell us about your business and what kind of website you need...',
    'consultation.cta': 'Get a free preview',
    'consultation.note': "No commitment. If you don't like the website — you don't pay.",
    'consultation.sending': 'Sending...',
    'consultation.or': 'or talk to our team directly',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Services',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    'footer.aboutLink': 'About XMonsters',
    // Stats
    'stats.websites': 'Websites Launched',
    'stats.clients': 'Happy Clients',
    'stats.years': 'Years Experience',
    'stats.title': 'Our',
    'stats.titleHighlight': 'Impact',
    'stats.subtitle': 'Delivering results for businesses worldwide'
  },
  ru: {
    // Navigation
    'nav.getStarted': 'Получить бесплатное превью',
    'nav.home': 'Главная',
    'nav.services': 'Цены',
    'nav.referral': 'Реферальная программа',
    'nav.privacy': 'Политика конфиденциальности',
    'nav.terms': 'Условия использования',
    // Cookie Banner
    'cookie.title': 'Мы используем cookies',
    'cookie.description': 'Мы используем cookies для улучшения вашего опыта на сайте, анализа трафика и персонализации контента.',
    'cookie.accept': 'Принять все',
    'cookie.decline': 'Отклонить',
    'cookie.learnMore': 'Подробнее',
    // Hero
    'hero.badge': 'Профессиональное веб-агентство',
    'hero.title': 'Современный сайт под ключ',
    'hero.titleHighlight': 'за €299',
    'hero.subtitle': 'Сначала мы делаем для вас бесплатное превью сайта, чтобы вы увидели, как будет выглядеть ваш будущий сайт. Затем вы пишете, что нужно изменить — мы вносим правки. Если вы хотите полноценно пользоваться сайтом, тогда вы оплачиваете €299.',
    'hero.bullet1': 'Бесплатное превью сайта до оплаты',
    'hero.bullet2': '€299 — стоимость разработки сайта, если вам всё подходит',
    'hero.bullet3': 'Работаем онлайн по всему миру',
    'hero.cta': 'Получить бесплатное превью',
    'hero.referral': 'Реферальная программа',
    // Pricing
    'pricing.title': 'Цены',
    'pricing.titleHighlight': 'и условия',
    'pricing.subtitle': 'Сначала бесплатное превью. Оплата только если вы хотите пользоваться сайтом.',
    'pricing.demo.title': 'Бесплатное превью сайта',
    'pricing.demo.label': 'БЕСПЛАТНО',
    'pricing.demo.desc': 'Покажем ваш будущий сайт до оплаты.',
    'pricing.demo.b1': 'Превью под ваш бизнес',
    'pricing.demo.b2': 'Можно попросить изменения',
    'pricing.demo.b3': 'Если не понравится — не платите',
    'pricing.demo.cta': 'Получить превью',
    'pricing.launch.title': 'Разработка сайта',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(единоразово)',
    'pricing.launch.desc': 'Если вам всё нравится — мы завершаем работу и вы получаете готовый сайт для использования.',
    'pricing.launch.b1': 'Дизайн для ПК и телефона',
    'pricing.launch.b2': 'Правки по вашему запросу',
    'pricing.launch.b3': 'Форма заявок + уведомления',
    'pricing.launch.cta': 'Заказать сайт за €299',
    // How It Works
    'how.title': 'Как это',
    'how.titleHighlight': 'работает',
    'how.step1.title': 'Расскажите о вашем бизнесе',
    'how.step1.note': 'Займёт 2 минуты — чем занимаетесь и какой сайт нужен',
    'how.step2.title': 'Мы делаем бесплатное превью сайта',
    'how.step2.note': 'Покажем дизайн и структуру. Пришлём ссылку в 1–3 дня',
    'how.step3.title': 'Вы пишете, что нужно изменить',
    'how.step3.note': 'Текст, блоки, стиль — мы всё правим под вас',
    'how.step4.title': 'Когда вас всё устраивает — вы оплачиваете €299',
    'how.step4.note': 'Оплата только после того, как вы скажете: "Да, мне подходит"',
    'how.step5.title': 'Подключаем домен и публикуем сайт',
    'how.step5.note': 'Сайт начинает работать и принимать заявки',
    'how.note': 'Если сайт вам не понравится — вы не платите.',
    // FAQ
    'faq.title': 'Часто задаваемые',
    'faq.titleHighlight': 'вопросы',
    'faq.subtitle': 'Всё, что нужно знать о работе с нами',
    'faq.q1': 'Что такое бесплатное превью?',
    'faq.a1': 'Это предварительная версия сайта, чтобы вы увидели дизайн и структуру до оплаты.',
    'faq.q2': 'Когда нужно платить €299?',
    'faq.a2': 'Только если вам понравился сайт и вы хотите им пользоваться.',
    'faq.q3': 'Можно ли попросить изменения?',
    'faq.a3': 'Да. Вы пишете правки — мы вносим изменения.',
    'faq.q4': 'Что я получу после оплаты?',
    'faq.a4': 'Готовый сайт, подключенный к вашему домену, с формой заявок.',
    'faq.q5': 'Вы работаете по всему миру?',
    'faq.a5': 'Да, полностью онлайн.',
    // Testimonials
    'testimonials.title': 'Что говорят',
    'testimonials.titleHighlight': 'клиенты',
    'testimonials.subtitle': 'Реальные отзывы компаний, которым мы помогли выйти в онлайн',
    'testimonials.r1.name': 'Анастасия',
    'testimonials.r1.text': 'Я думала, что за €299 сделают что-то очень простое. Но мне скинули ссылку на сайт — он выглядел реально дорого. Я попросила поменять пару фраз и кнопку, мне всё поправили. Заплатила только когда сказала: "Да, хочу пользоваться и подключать домен". До этого ничего не платила.',
    'testimonials.r1.business': 'Мастер бровей',
    'testimonials.r2.name': 'Дмитрий',
    'testimonials.r2.text': 'Мне нужен был обычный сайт: услуги, цены и кнопка "Написать". Я описал, чем занимаюсь — мне сделали сайт и дали посмотреть. Я сказал, что надо исправить — исправили. Деньги попросили только когда я подтвердил, что хочу запускать сайт на своём домене. Вот так и должно быть.',
    'testimonials.r2.business': 'Мастер по сантехнике',
    'testimonials.r3.name': 'Сергей',
    'testimonials.r3.text': 'Мне понравилось, что никто не просит оплату заранее. Сначала показали сайт, я спокойно посмотрел, попросил сделать тексты более понятными. После правок сайт стал прям топ: красиво, спокойно, аккуратно. И только когда я решил, что буду реально использовать его для клиентов — оплатил €299.',
    'testimonials.r3.business': 'Психолог',
    // Consultation
    'consultation.title': 'Получите бесплатное',
    'consultation.titleHighlight': 'превью',
    'consultation.subtitle': 'Расскажите, что вам нужно — мы пришлём ссылку на превью.',
    'consultation.benefit1': 'Бесплатный просмотр',
    'consultation.benefit2': 'Быстрая доставка',
    'consultation.benefit3': 'Без обязательств',
    'consultation.name': 'Имя',
    'consultation.namePlaceholder': 'Ваше имя',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Описание проекта',
    'consultation.descPlaceholder': 'Расскажите о вашем бизнесе и о том, какой сайт вам нужен…',
    'consultation.cta': 'Получить бесплатное превью',
    'consultation.note': 'Без обязательств. Если сайт не понравится — вы не платите.',
    'consultation.sending': 'Отправка...',
    'consultation.or': 'или напишите нам напрямую',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Услуги',
    'footer.company': 'Компания',
    'footer.legal': 'Юридическое',
    'footer.contact': 'Контакты',
    'footer.rights': 'Все права защищены.',
    'footer.aboutLink': 'О компании',
    // Stats
    'stats.websites': 'Сайтов запущено',
    'stats.clients': 'Довольных клиентов',
    'stats.years': 'Лет опыта',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вклад',
    'stats.subtitle': 'Результаты для бизнеса по всему миру'
  },
  ua: {
    // Navigation
    'nav.getStarted': "Отримати безкоштовне прев'ю",
    'nav.home': 'Головна',
    'nav.services': 'Ціни',
    'nav.referral': 'Реферальна програма',
    'nav.privacy': 'Політика конфіденційності',
    'nav.terms': 'Умови використання',
    // Cookie Banner
    'cookie.title': 'Ми використовуємо cookies',
    'cookie.description': 'Ми використовуємо cookies для покращення вашого досвіду на сайті, аналізу трафіку та персоналізації контенту.',
    'cookie.accept': 'Прийняти все',
    'cookie.decline': 'Відхилити',
    'cookie.learnMore': 'Детальніше',
    // Hero
    'hero.badge': 'Професійне веб-агентство',
    'hero.title': 'Сучасний сайт під ключ',
    'hero.titleHighlight': 'за €299',
    'hero.subtitle': "Спочатку ми робимо для вас безкоштовне прев'ю сайту, щоб ви побачили, як виглядатиме ваш майбутній сайт. Потім ви пишете, що потрібно змінити — ми вносимо правки. Якщо ви хочете повноцінно користуватися сайтом, тоді ви оплачуєте €299.",
    'hero.bullet1': "Безкоштовне прев'ю сайту до оплати",
    'hero.bullet2': '€299 — вартість розробки сайту, якщо вам усе підходить',
    'hero.bullet3': 'Працюємо онлайн по всьому світу',
    'hero.cta': "Отримати безкоштовне прев'ю",
    'hero.referral': 'Реферальна програма',
    // Pricing
    'pricing.title': 'Ціни',
    'pricing.titleHighlight': 'та умови',
    'pricing.subtitle': "Спочатку безкоштовне прев'ю. Оплата тільки якщо хочете користуватися сайтом.",
    'pricing.demo.title': "Безкоштовне прев'ю сайту",
    'pricing.demo.label': 'БЕЗКОШТОВНО',
    'pricing.demo.desc': 'Покажемо ваш майбутній сайт до оплати.',
    'pricing.demo.b1': "Прев'ю під ваш бізнес",
    'pricing.demo.b2': 'Можна попросити зміни',
    'pricing.demo.b3': 'Якщо не сподобається — не платите',
    'pricing.demo.cta': "Отримати прев'ю",
    'pricing.launch.title': 'Розробка сайту',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(разово)',
    'pricing.launch.desc': 'Якщо вам усе подобається — завершуємо роботу, і ви отримуєте готовий сайт для користування.',
    'pricing.launch.b1': 'Дизайн для ПК і телефону',
    'pricing.launch.b2': 'Правки за вашим запитом',
    'pricing.launch.b3': 'Форма заявок + сповіщення',
    'pricing.launch.cta': 'Замовити сайт за €299',
    // How It Works
    'how.title': 'Як це',
    'how.titleHighlight': 'працює',
    'how.step1.title': 'Розкажіть про ваш бізнес',
    'how.step1.note': 'Займе 2 хвилини — чим займаєтесь і який сайт потрібен',
    'how.step2.title': "Ми робимо безкоштовне прев'ю сайту",
    'how.step2.note': 'Покажемо дизайн і структуру. Надішлемо посилання за 1–3 дні',
    'how.step3.title': 'Ви пишете, що потрібно змінити',
    'how.step3.note': 'Текст, блоки, стиль — ми все підправляємо під вас',
    'how.step4.title': 'Коли вам усе підходить — ви оплачуєте €299',
    'how.step4.note': 'Оплата тільки після того, як ви скажете: "Так, мені підходить"',
    'how.step5.title': 'Підключаємо домен і публікуємо сайт',
    'how.step5.note': 'Сайт починає працювати та приймати заявки',
    'how.note': 'Якщо сайт вам не сподобається — ви не платите.',
    // FAQ
    'faq.title': 'Часті',
    'faq.titleHighlight': 'питання',
    'faq.subtitle': 'Все, що потрібно знати про роботу з нами',
    'faq.q1': "Що таке безкоштовне прев'ю?",
    'faq.a1': 'Це попередня версія сайту, щоб ви побачили дизайн і структуру до оплати.',
    'faq.q2': 'Коли потрібно платити €299?',
    'faq.a2': 'Тільки якщо сайт вам сподобався і ви хочете ним користуватися.',
    'faq.q3': 'Чи можна попросити зміни?',
    'faq.a3': 'Так. Ви пишете правки — ми вносимо зміни.',
    'faq.q4': 'Що я отримаю після оплати?',
    'faq.a4': 'Готовий сайт, підключений до вашого домену, з формою заявок.',
    'faq.q5': 'Ви працюєте по всьому світу?',
    'faq.a5': 'Так, працюємо онлайн.',
    // Testimonials
    'testimonials.title': 'Що кажуть',
    'testimonials.titleHighlight': 'клієнти',
    'testimonials.subtitle': 'Реальні відгуки від бізнесів, яким ми допомогли вийти онлайн',
    'testimonials.r1.name': 'Оксана',
    'testimonials.r1.text': 'Я не хотіла платити, поки не побачу результат. Мені зробили сайт і надіслали посилання, щоб я подивилася. Я написала, що змінити — все підправили. Оплатила €299 тільки коли вирішила користуватися сайтом і підключити свій домен.',
    'testimonials.r1.business': 'Майстер манікюру',
    'testimonials.r2.name': 'Віталій',
    'testimonials.r2.text': 'Чесно, був шок: за €299 такий дизайн — дуже гідно. Спочатку показали сайт без оплати, я попросив змінити пару блоків і кнопку. Платиш тільки якщо реально запускаєш сайт для роботи.',
    'testimonials.r2.business': 'Ремонт техніки',
    'testimonials.r3.name': 'Андрій',
    'testimonials.r3.text': 'Мені важливо було не ризикувати. Тут все просто: спочатку роблять сайт і показують, потім правки, і тільки якщо хочеш використовувати для клієнтів — тоді оплачуєш. Дуже чесно.',
    'testimonials.r3.business': 'Юрист',
    // Consultation
    'consultation.title': 'Отримайте безкоштовне',
    'consultation.titleHighlight': "прев'ю",
    'consultation.subtitle': "Скажіть, що вам потрібно — ми надішлемо посилання на прев'ю.",
    'consultation.benefit1': 'Безкоштовний перегляд',
    'consultation.benefit2': 'Швидка доставка',
    'consultation.benefit3': "Без зобов'язань",
    'consultation.name': "Ім'я",
    'consultation.namePlaceholder': "Ваше ім'я",
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'ваш@email.com',
    'consultation.desc': 'Опис проєкту',
    'consultation.descPlaceholder': 'Розкажіть про ваш бізнес і який сайт вам потрібен...',
    'consultation.cta': "Отримати безкоштовне прев'ю",
    'consultation.note': "Без зобов'язань. Якщо сайт не сподобається — ви не платите.",
    'consultation.sending': 'Відправка...',
    'consultation.or': 'або напишіть нам напряму',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Послуги',
    'footer.company': 'Компанія',
    'footer.legal': 'Юридичне',
    'footer.contact': 'Контакти',
    'footer.rights': 'Всі права захищені.',
    'footer.aboutLink': 'Про нас',
    // Stats
    'stats.websites': 'Сайтів запущено',
    'stats.clients': 'Задоволених клієнтів',
    'stats.years': 'Років досвіду',
    'stats.title': 'Наш',
    'stats.titleHighlight': 'Вплив',
    'stats.subtitle': 'Результати для бізнесу по всьому світу'
  },
  pl: {
    // Navigation
    'nav.getStarted': 'Otrzymaj bezpłatny podgląd',
    'nav.home': 'Strona główna',
    'nav.services': 'Cennik',
    'nav.referral': 'Program poleceń',
    'nav.privacy': 'Polityka prywatności',
    'nav.terms': 'Warunki użytkowania',
    // Cookie Banner
    'cookie.title': 'Używamy plików cookie',
    'cookie.description': 'Używamy plików cookie, aby poprawić Twoje doświadczenia na naszej stronie, analizować ruch i personalizować treści.',
    'cookie.accept': 'Zaakceptuj wszystkie',
    'cookie.decline': 'Odrzuć',
    'cookie.learnMore': 'Dowiedz się więcej',
    // Hero
    'hero.badge': 'Profesjonalna agencja webowa',
    'hero.title': 'Nowoczesna strona internetowa',
    'hero.titleHighlight': 'za €299',
    'hero.subtitle': 'Najpierw przygotujemy bezpłatny podgląd strony, żebyś zobaczył/a, jak będzie wyglądać Twoja przyszła strona. Potem piszesz, co trzeba zmienić — my wprowadzamy poprawki. Jeśli chcesz korzystać ze strony, wtedy płacisz €299.',
    'hero.bullet1': 'Bezpłatny podgląd strony przed płatnością',
    'hero.bullet2': '€299 — opłata za wykonanie strony, jeśli Ci odpowiada',
    'hero.bullet3': 'Pracujemy zdalnie na całym świecie',
    'hero.cta': 'Otrzymaj bezpłatny podgląd',
    'hero.referral': 'Program poleceń',
    // Pricing
    'pricing.title': 'Cennik',
    'pricing.titleHighlight': 'i warunki',
    'pricing.subtitle': 'Zaczynamy od bezpłatnego podglądu. Płacisz tylko, jeśli chcesz korzystać ze strony.',
    'pricing.demo.title': 'Bezpłatny podgląd strony',
    'pricing.demo.label': 'BEZPŁATNIE',
    'pricing.demo.desc': 'Zobacz swoją przyszłą stronę przed płatnością.',
    'pricing.demo.b1': 'Podgląd dopasowany do Twojego biznesu',
    'pricing.demo.b2': 'Możesz poprosić o zmiany',
    'pricing.demo.b3': 'Nie pasuje — nie płacisz',
    'pricing.demo.cta': 'Otrzymaj podgląd',
    'pricing.launch.title': 'Wykonanie strony',
    'pricing.launch.price': '€299',
    'pricing.launch.period': '(jednorazowo)',
    'pricing.launch.desc': 'Jeśli efekt Ci odpowiada, finalizujemy stronę i dostajesz gotową wersję do korzystania.',
    'pricing.launch.b1': 'Design na komputer i telefon',
    'pricing.launch.b2': 'Poprawki na podstawie Twoich uwag',
    'pricing.launch.b3': 'Formularz kontaktowy + powiadomienia',
    'pricing.launch.cta': 'Zamów stronę za €299',
    // How It Works
    'how.title': 'Jak to',
    'how.titleHighlight': 'działa',
    'how.step1.title': 'Opowiedz o swoim biznesie',
    'how.step1.note': '2 minuty — czym się zajmujesz i jakiej strony potrzebujesz',
    'how.step2.title': 'Tworzymy bezpłatny podgląd strony',
    'how.step2.note': 'Pokazujemy design i strukturę. Link w 1–3 dni',
    'how.step3.title': 'Piszesz, co trzeba zmienić',
    'how.step3.note': 'Tekst, sekcje, styl — wprowadzamy poprawki',
    'how.step4.title': 'Gdy wszystko Ci pasuje, płacisz €299',
    'how.step4.note': 'Płatność dopiero, gdy powiesz: „Tak, pasuje mi"',
    'how.step5.title': 'Podłączamy domenę i publikujemy stronę',
    'how.step5.note': 'Strona działa i zbiera zapytania',
    'how.note': 'Jeśli strona Ci się nie spodoba — nie płacisz.',
    // FAQ
    'faq.title': 'Często zadawane',
    'faq.titleHighlight': 'pytania',
    'faq.subtitle': 'Wszystko, co musisz wiedzieć o pracy z nami',
    'faq.q1': 'Co to jest bezpłatny podgląd?',
    'faq.a1': 'To wstępna wersja strony, abyś zobaczył/a design i strukturę przed płatnością.',
    'faq.q2': 'Kiedy płacę €299?',
    'faq.a2': 'Tylko jeśli strona Ci się podoba i chcesz z niej korzystać.',
    'faq.q3': 'Czy mogę poprosić o zmiany?',
    'faq.a3': 'Tak. Wysyłasz uwagi — my wprowadzamy poprawki.',
    'faq.q4': 'Co dostanę po płatności?',
    'faq.a4': 'Gotową stronę podłączoną do Twojej domeny, z formularzem kontaktowym.',
    'faq.q5': 'Czy pracujecie na całym świecie?',
    'faq.a5': 'Tak, w pełni zdalnie.',
    // Testimonials
    'testimonials.title': 'Co mówią',
    'testimonials.titleHighlight': 'klienci',
    'testimonials.subtitle': 'Prawdziwe opinie od firm, którym pomogliśmy wejść online',
    'testimonials.r1.name': 'Kasia',
    'testimonials.r1.text': 'Byłam w szoku, że za €299 strona wygląda tak dobrze. Najpierw zrobili stronę i wysłali link do sprawdzenia. Poprosiłam o drobne zmiany i dopiero gdy zdecydowałam, że chcę z niej korzystać i podłączyć domenę — zapłaciłam.',
    'testimonials.r1.business': 'Kosmetyczka',
    'testimonials.r2.name': 'Piotr',
    'testimonials.r2.text': 'Bez płacenia на start. Najpierw pokазали gotową stronę, wprowadзили poprawки и платность является только тогда, когда вы хотите использовать сайт в своей компании.',
    'testimonials.r2.business': 'Hydraulik',
    'testimonials.r3.name': 'Tomek',
    'testimonials.r3.text': 'Podoba mi się uczciwe podejście: najpierw efekt, потом decyzя. Плациш только если действительно хочешь использовать.',
    'testimonials.r3.business': 'Trener personalny',
    // Consultation
    'consultation.title': 'Otrzymaj bezpłatny',
    'consultation.titleHighlight': 'podgląd',
    'consultation.subtitle': 'Napisz, czego potrzebujesz — wyślemy Ci link do podglądu.',
    'consultation.benefit1': 'Bezpłatny podgląd',
    'consultation.benefit2': 'Szybka dostawa',
    'consultation.benefit3': 'Bez zobowiązań',
    'consultation.name': 'Imię',
    'consultation.namePlaceholder': 'Twoje imię',
    'consultation.email': 'Email',
    'consultation.emailPlaceholder': 'twoj@email.com',
    'consultation.desc': 'Opis projektu',
    'consultation.descPlaceholder': 'Opowiedz nam o swoim biznesie i o tym, jakiej strony potrzebujesz…',
    'consultation.cta': 'Otrzymaj bezpłatny podgląd',
    'consultation.note': 'Bez zobowiązań. Jeśli strona Ci się nie spodoba — nie płacisz.',
    'consultation.sending': 'Wysyłanie...',
    'consultation.or': 'lub napisz do nas bezpośrednio',
    'consultation.telegram': 'Telegram',
    'consultation.whatsapp': 'WhatsApp',
    // Footer
    'footer.services': 'Usługi',
    'footer.company': 'Firma',
    'footer.legal': 'Prawne',
    'footer.contact': 'Kontakt',
    'footer.rights': 'Wszelkie prawa zastrzeżone.',
    'footer.aboutLink': 'O nas',
    // Stats
    'stats.websites': 'Uruchomionych stron',
    'stats.clients': 'Zadowolonych klientów',
    'stats.years': 'Lat doświadczenia',
    'stats.title': 'Nasz',
    'stats.titleHighlight': 'Wpływ',
    'stats.subtitle': 'Wyniki dla firm na całym świecie'
  }
};
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [language, setLanguage] = useState<Language>('en');
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/ru')) {
      setLanguage('ru');
    } else if (path.startsWith('/ua')) {
      setLanguage('ua');
    } else if (path.startsWith('/uk')) {
      // Handle legacy 'uk' prefix as 'ua'
      setLanguage('ua');
    } else if (path.startsWith('/pl')) {
      setLanguage('pl');
    } else {
      setLanguage('en');
    }
  }, []);
  const t = (key: string): string => {
    return translations[language][key as keyof (typeof translations)['en']] || key;
  };
  const getPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    if (language === 'ru') {
      return `/ru/${cleanPath}`;
    } else if (language === 'ua') {
      return `/ua/${cleanPath}`;
    } else if (language === 'pl') {
      return `/pl/${cleanPath}`;
    }
    return `/${cleanPath}`;
  };
  return <LanguageContext.Provider value={{
    language,
    setLanguage,
    t,
    getPath
  }} data-id="element-283">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}